import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp,
  Search,
  Filter,
  Download,
  Building2,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Award,
  Layers,
  BarChart3,
  Eye,
  School
} from 'lucide-react';
import { SchoolAcademicProfileView } from './SchoolAcademicProfileView';

interface SchoolRecord {
  id: number;
  school_code: string;
  school_name: string;
  block: string;
  category: string;
  total_students: number;
  evaluated_count: number;
  avg_score: number;
  band_label: string;
  color_code: string;
  rank: number;
}

export const SchoolPerformancePage: React.FC = () => {
  const { user, fetchWithAuth } = useAuth();
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [schools, setSchools] = useState<SchoolRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedMonth, setSelectedMonth] = useState<string>('August');
  const [selectedBlock, setSelectedBlock] = useState<string>(user?.role === 'beo' && user?.block ? user.block : 'all');
  const [selectedBand, setSelectedBand] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('score_desc');
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  const months = ['April', 'May', 'July', 'August', 'September', 'October', 'November', 'December', 'January', 'February'];
  const blocks = ['Nuh', 'Nagina', 'Taoru', 'Ferozepur Jhirka', 'Punhana'];

  const bands = [
    { label: 'All Categories', value: 'all' },
    { label: 'Outstanding (90–100%)', value: 'outstanding', color: 'bg-emerald-100 text-emerald-800' },
    { label: 'Good (75–89%)', value: 'good', color: 'bg-teal-100 text-teal-800' },
    { label: 'Satisfactory (60–74%)', value: 'satisfactory', color: 'bg-blue-100 text-blue-800' },
    { label: 'Needs Improvement (40–59%)', value: 'needs_improvement', color: 'bg-amber-100 text-amber-800' },
    { label: 'Critical (0–39%)', value: 'critical', color: 'bg-rose-100 text-rose-800' }
  ];

  const fetchSchoolPerformance = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        month: selectedMonth,
        block: selectedBlock,
        category: selectedBand,
        search: searchTerm,
        sort: sortBy,
        page: page.toString(),
        limit: '15'
      });

      const res = await fetchWithAuth(`/api/deo/school-performance-table?${params.toString()}`).then(r => r.json());
      if (res.success && res.data) {
        setSchools(res.data.schools || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalCount(res.data.pagination?.total || 0);
      } else {
        setError(res.message || 'Failed to load school performance data');
      }
    } catch (err: any) {
      setError(err.message || 'Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchoolPerformance();
  }, [selectedMonth, selectedBlock, selectedBand, sortBy, page]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchSchoolPerformance();
  };

  const handleExportCSV = () => {
    if (schools.length === 0) return;
    const rows = [
      ['District Education Office, Nuh — School Performance Scorecard'],
      ['Month', selectedMonth, 'Block', selectedBlock],
      [],
      ['Rank', 'School Code', 'School Name', 'Block', 'Category', 'Enrolled Students', 'Average Score (%)', 'Performance Category'],
      ...schools.map(s => [
        s.rank || '',
        s.school_code,
        s.school_name,
        s.block,
        s.category,
        s.total_students,
        `${s.avg_score}%`,
        s.band_label || (s.avg_score >= 75 ? 'Good' : s.avg_score >= 60 ? 'Satisfactory' : s.avg_score >= 40 ? 'Needs Improvement' : 'Critical')
      ])
    ];

    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map(e => e.map(cell => `"${cell}"`).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `School_Performance_${selectedMonth}_${selectedBlock}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getBandBadge = (score: number) => {
    if (score >= 90) {
      return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300">90-100% Outstanding</span>;
    }
    if (score >= 75) {
      return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-300">75-89% Good</span>;
    }
    if (score >= 60) {
      return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800 border border-blue-300">60-74% Satisfactory</span>;
    }
    if (score >= 40) {
      return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300">40-59% Needs Improvement</span>;
    }
    return <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-rose-100 text-rose-800 border border-rose-300">0-39% Critical</span>;
  };

  if (selectedSchoolId !== null) {
    return (
      <SchoolAcademicProfileView
        schoolId={selectedSchoolId}
        onBack={() => setSelectedSchoolId(null)}
        onSelectSchool={(id) => setSelectedSchoolId(id)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Top Banner */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-blue-900" />
            <h1 className="text-base font-bold text-slate-900">School Performance & Ranking Scorecard</h1>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            District-wide ranking and 5-tier categorization based on validated Monthly Assessment & SAT marks
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={fetchSchoolPerformance}
            className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded border border-slate-300 transition"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={handleExportCSV}
            className="flex items-center space-x-1 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded transition shadow-xs"
          >
            <Download className="w-3.5 h-3.5" />
            <span>Export CSV</span>
          </button>
        </div>
      </div>

      {/* 5-Tier Summary Legend Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
        <div
          onClick={() => { setSelectedBand('outstanding'); setPage(1); }}
          className={`p-3 rounded-lg border cursor-pointer transition ${
            selectedBand === 'outstanding' ? 'bg-emerald-50 border-emerald-500 ring-2 ring-emerald-400' : 'bg-white border-slate-200 hover:border-emerald-300'
          }`}
        >
          <div className="text-[10px] font-bold text-emerald-800 uppercase">Outstanding</div>
          <div className="text-lg font-black text-emerald-900 mt-0.5">90 – 100%</div>
          <div className="text-[10px] text-slate-500 mt-1">Grade A+ (Distinction)</div>
        </div>

        <div
          onClick={() => { setSelectedBand('good'); setPage(1); }}
          className={`p-3 rounded-lg border cursor-pointer transition ${
            selectedBand === 'good' ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-400' : 'bg-white border-slate-200 hover:border-teal-300'
          }`}
        >
          <div className="text-[10px] font-bold text-teal-800 uppercase">Good</div>
          <div className="text-lg font-black text-teal-900 mt-0.5">75 – 89%</div>
          <div className="text-[10px] text-slate-500 mt-1">Grade A (Proficient)</div>
        </div>

        <div
          onClick={() => { setSelectedBand('satisfactory'); setPage(1); }}
          className={`p-3 rounded-lg border cursor-pointer transition ${
            selectedBand === 'satisfactory' ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-400' : 'bg-white border-slate-200 hover:border-blue-300'
          }`}
        >
          <div className="text-[10px] font-bold text-blue-800 uppercase">Satisfactory</div>
          <div className="text-lg font-black text-blue-900 mt-0.5">60 – 74%</div>
          <div className="text-[10px] text-slate-500 mt-1">Grade B (Average)</div>
        </div>

        <div
          onClick={() => { setSelectedBand('needs_improvement'); setPage(1); }}
          className={`p-3 rounded-lg border cursor-pointer transition ${
            selectedBand === 'needs_improvement' ? 'bg-amber-50 border-amber-500 ring-2 ring-amber-400' : 'bg-white border-slate-200 hover:border-amber-300'
          }`}
        >
          <div className="text-[10px] font-bold text-amber-800 uppercase">Needs Improvement</div>
          <div className="text-lg font-black text-amber-900 mt-0.5">40 – 59%</div>
          <div className="text-[10px] text-slate-500 mt-1">Grade C (Support Required)</div>
        </div>

        <div
          onClick={() => { setSelectedBand('critical'); setPage(1); }}
          className={`p-3 rounded-lg border cursor-pointer transition col-span-2 sm:col-span-1 ${
            selectedBand === 'critical' ? 'bg-rose-50 border-rose-500 ring-2 ring-rose-400' : 'bg-white border-slate-200 hover:border-rose-300'
          }`}
        >
          <div className="text-[10px] font-bold text-rose-800 uppercase">Critical / Remedial</div>
          <div className="text-lg font-black text-rose-900 mt-0.5">0 – 39%</div>
          <div className="text-[10px] text-rose-600 mt-1 font-semibold">Priority Intervention</div>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-3.5 rounded-lg border border-slate-200 shadow-xs">
        <form onSubmit={handleSearchSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Month Selector */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Assessment Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => { setSelectedMonth(e.target.value); setPage(1); }}
              className="w-full text-xs font-semibold bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-slate-800 focus:outline-blue-800"
            >
              {months.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Block Selector */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Block
            </label>
            <select
              value={selectedBlock}
              disabled={user?.role === 'beo'}
              onChange={(e) => { setSelectedBlock(e.target.value); setPage(1); }}
              className="w-full text-xs font-semibold bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-slate-800 focus:outline-blue-800 disabled:opacity-75"
            >
              <option value="all">All Blocks (District-wide)</option>
              {blocks.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>

          {/* Performance Category Selector */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Performance Band
            </label>
            <select
              value={selectedBand}
              onChange={(e) => { setSelectedBand(e.target.value); setPage(1); }}
              className="w-full text-xs font-semibold bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-slate-800 focus:outline-blue-800"
            >
              {bands.map(b => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
              Sort By
            </label>
            <select
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
              className="w-full text-xs font-semibold bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-slate-800 focus:outline-blue-800"
            >
              <option value="score_desc">Highest Score First</option>
              <option value="score_asc">Lowest Score First (Intervention)</option>
              <option value="name_asc">School Name (A–Z)</option>
              <option value="students_desc">Most Students</option>
            </select>
          </div>

          {/* Search Box */}
          <div className="flex items-end space-x-2">
            <div className="relative flex-1">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                Search School
              </label>
              <input
                type="text"
                placeholder="Name or School Code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full text-xs bg-slate-50 border border-slate-300 rounded px-2.5 py-1.5 text-slate-800 placeholder-slate-400 focus:outline-blue-800"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-1.5 bg-[#0B1E38] hover:bg-[#15345e] text-white text-xs font-bold rounded transition"
            >
              Search
            </button>
          </div>
        </form>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-xs overflow-hidden">
        <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex justify-between items-center text-xs">
          <div className="font-bold text-slate-800">
            Showing <span className="text-blue-900">{schools.length}</span> of <span className="text-blue-900">{totalCount}</span> Schools
          </div>
          <div className="text-[11px] text-slate-500">
            {selectedBlock === 'all' ? 'All Blocks' : `Block: ${selectedBlock}`} • Month: {selectedMonth}
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-3 border-blue-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-xs font-semibold text-slate-600">Loading school performance records...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center bg-rose-50 text-rose-800">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-rose-600" />
            <p className="text-xs font-bold">{error}</p>
          </div>
        ) : schools.length === 0 ? (
          <div className="p-12 text-center text-slate-500 text-xs">
            No schools found matching the selected filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-[#0B1E38] text-white font-semibold">
                <tr>
                  <th className="py-2.5 px-3.5 text-center w-12">#</th>
                  <th className="py-2.5 px-3.5">School Code</th>
                  <th className="py-2.5 px-3.5">School Name</th>
                  <th className="py-2.5 px-3.5">Block</th>
                  <th className="py-2.5 px-3.5">Category</th>
                  <th className="py-2.5 px-3.5 text-right">Enrolled Students</th>
                  <th className="py-2.5 px-3.5 text-right">Average Score (%)</th>
                  <th className="py-2.5 px-3.5 text-center">Performance Band</th>
                  <th className="py-2.5 px-3.5 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 font-medium text-slate-800">
                {schools.map((school, index) => {
                  const displayRank = (page - 1) * 15 + index + 1;
                  return (
                    <tr key={school.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-2.5 px-3.5 text-center font-bold text-slate-600">
                        {displayRank}
                      </td>
                      <td className="py-2.5 px-3.5 font-mono text-[11px] font-bold text-blue-900">
                        {school.school_code}
                      </td>
                      <td className="py-2.5 px-3.5 font-bold text-slate-900">
                        <button
                          onClick={() => setSelectedSchoolId(school.id)}
                          className="text-left font-bold text-[#0F2C59] hover:underline hover:text-blue-700 flex items-center space-x-1"
                          title="Open Full Academic Performance Profile"
                        >
                          <span>{school.school_name}</span>
                          <ChevronRight className="w-3 h-3 text-slate-400 shrink-0" />
                        </button>
                      </td>
                      <td className="py-2.5 px-3.5 text-slate-700">
                        {school.block}
                      </td>
                      <td className="py-2.5 px-3.5 text-slate-600 text-[11px]">
                        {school.category}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-semibold text-slate-700">
                        {(school.total_students || 0).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3.5 text-right font-black text-sm text-[#0B1E38]">
                        {Number(school.avg_score).toFixed(1)}%
                      </td>
                      <td className="py-2.5 px-3.5 text-center">
                        {getBandBadge(Number(school.avg_score))}
                      </td>
                      <td className="py-2.5 px-3.5 text-center">
                        <button
                          onClick={() => setSelectedSchoolId(school.id)}
                          className="px-2.5 py-1 bg-[#0F2C59] hover:bg-[#1E3A8A] text-white rounded font-bold text-[10px] transition flex items-center space-x-1 mx-auto shadow-2xs"
                        >
                          <Award className="w-3 h-3 text-amber-400" />
                          <span>Profile</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Footer */}
        <div className="p-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between text-xs">
          <div className="text-slate-600 font-medium">
            Page <span className="font-bold text-slate-900">{page}</span> of <span className="font-bold text-slate-900">{totalPages}</span>
          </div>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-2.5 py-1 bg-white border border-slate-300 rounded text-slate-700 font-medium hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white transition"
            >
              <ChevronLeft className="w-3.5 h-3.5 inline" /> Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="px-2.5 py-1 bg-white border border-slate-300 rounded text-slate-700 font-medium hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-white transition"
            >
              Next <ChevronRight className="w-3.5 h-3.5 inline" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
